import type { IAdminModule } from "viewModels/types";
import * as sharedServices from "@emanprague/shared-services";
import { bound } from "@frui.ts/helpers";
import { BusyWatcher, Router, ScreenBase, watchBusy } from "@frui.ts/screens";
import { observable, runInAction } from "mobx";
import AdminRepository from "repositories/adminRepository";
import * as adminStatistics from "entities/adminStatistics";
import type { AdminStatisticsCard } from "entities/adminStatistics";
import AdminStatisticsFilter from "models/adminStatisticsFilter";

const navigationName = "statistics";

@Router.registerRoute({ name: Router.Self, route: `${navigationName}` })
export default class StatisticsPageViewModel extends ScreenBase implements IAdminModule {
  navigationName = navigationName;
  orderIndex = 250;
  menuPart = "admin";
  busyWatcher = new BusyWatcher();

  // Default filter values are set in the class
  filter: AdminStatisticsFilter = new AdminStatisticsFilter();

  @observable
  statCardsData?: Record<string, AdminStatisticsCard>;

  @observable
  lastLoginGraphData?: adminStatistics.AdminStatisticsGraphData;

  @observable
  registrationGraphData?: adminStatistics.AdminStatisticsGraphData;

  constructor(
    public localization: sharedServices.ILocalizationService,
    private adminRepository: AdminRepository
  ) {
    super();

    this.name = this.translate("title");
  }

  protected onInitialize(): Promise<any> {
    return this.loadData();
  }

  @bound
  @watchBusy
  async loadData() {
    const response = await this.adminRepository.getStatistics(this.filter);

    if (response.success) {
      runInAction(() => {
        this.statCardsData = response.payload.statCards;
        this.lastLoginGraphData = response.payload.lastLoginGraphData;
        this.registrationGraphData = response.payload.registrationGraphData;
      });
    }
  }

  @bound setFilter(filter: AdminStatisticsFilter) {
    this.filter = AdminStatisticsFilter.fromObject(filter); // recreate filters with new values
    void this.loadData();
  }

  @bound translate(key: string) {
    return this.localization.translateGeneral(`admin.statistics.${key}`);
  }

  @bound translateGeneral(key: string) {
    return this.localization.translateGeneral(`general.${key}`);
  }

  @bound translateCardDescription(key: string) {
    return this.localization.translateGeneral(`admin.statistics.cards.${key}.description`, {
      days: this.filter.period?.toString() ?? "??",
    });
  }

  @bound formatNumber(value: number) {
    return this.localization.formatNumber(value);
  }

  @bound formatDate(date: Date) {
    return this.localization.formatDate(date);
  }
}
